import { useCallback, useEffect } from "react";
import type { Suggestion } from "@/hooks/useFilterSearch";

interface UseKeyboardNavProps {
  opened: boolean;
  suggestions: Suggestion[];
  highlighted: number | null;
  query: string;
  searchPath: string;
  selectedLength: number;
  onSetOpened: (opened: boolean) => void;
  onSetHighlighted: (index: number | null | ((prev: number | null) => number | null)) => void;
  onSelect: (suggestion: Suggestion, fromDropdown: boolean) => void;
  onKeywordAdd: () => void;
  onNavigate: (path: string) => void;
  onNavigateWithHighlighted: () => boolean;
}

export const useKeyboardNav = ({
  opened,
  suggestions,
  highlighted,
  query,
  searchPath,
  selectedLength,
  onSetOpened,
  onSetHighlighted,
  onSelect,
  onKeywordAdd,
  onNavigate,
  onNavigateWithHighlighted,
}: UseKeyboardNavProps) => {
  // Scroll highlighted item into view
  useEffect(() => {
    if (highlighted == null) return;
    const scrollRef = document.querySelector("[data-scrollarea-viewport]") as HTMLElement;
    if (!scrollRef) return;
    const el = scrollRef.querySelectorAll("li")[highlighted];
    if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [highlighted]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!opened) {
        if (e.key === "ArrowDown" && suggestions.length > 0) {
          onSetOpened(true);
          onSetHighlighted(0);
          e.preventDefault();
        }
        if (e.key === "Enter") {
          e.preventDefault();
          if (onNavigateWithHighlighted()) return;
          if (!query.trim()) {
            onNavigate(searchPath);
          } else {
            onKeywordAdd();
          }
        }
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        onSetHighlighted((idx) => (idx == null ? 0 : Math.min(idx + 1, suggestions.length - 1)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        onSetHighlighted((idx) => (idx == null ? 0 : Math.max(idx - 1, 0)));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlighted != null && suggestions[highlighted]) {
          onSelect(suggestions[highlighted], true);
        } else if (!query.trim() && selectedLength === 0) {
          onNavigate(searchPath);
        } else {
          onKeywordAdd();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        onSetOpened(false);
        onSetHighlighted(null);
      }
    },
    [
      opened,
      suggestions,
      highlighted,
      query,
      searchPath,
      selectedLength,
      onSetOpened,
      onSetHighlighted,
      onSelect,
      onKeywordAdd,
      onNavigate,
      onNavigateWithHighlighted,
    ]
  );

  return { onKeyDown };
};
